import { useSelector } from "react-redux";
import { Link, Navigate } from "react-router-dom";
import {
  RiRobot2Line,
} from "react-icons/ri";
import {
  MdOutlineMenuBook,
  MdEvent,
  MdOutlineDashboard,
  MdReceiptLong,
} from "react-icons/md";
import { FaBullhorn } from "react-icons/fa";
import { BsFillPersonLinesFill } from "react-icons/bs";
import { FiFileText } from "react-icons/fi";
import { LuCommand } from "react-icons/lu";

import { selectPermissionSettings } from "../../store/slice/permissionSettings";
import RightPanelLayout from "../Layout/RightPanelLayout";
import styles from "./style/KeystoneAccountHome.module.scss";

const ACCOUNT_HOME_CONFIG = {
  "workspace+manage@adbert.com.tw": {
    label: "管理層",
    headline: "歡迎回來，管理層",
    description: "您擁有 Keystone 全部模組的管理權限，請選擇要管理的項目。",
    quickLinks: [
      {
        name: "Keystone 中控",
        link: "/dashboard/tdp/menu",
        icon: <MdOutlineDashboard />,
      },
      {
        name: "START-ONE 報名",
        link: "/admin/form/ai-agent",
        icon: <RiRobot2Line />,
      },
      {
        name: "活動管理",
        link: "/dashboard/tdp/events",
        icon: <MdEvent />,
      },
      {
        name: "公告管理",
        link: "/dashboard/tdp/system-announcements",
        icon: <FaBullhorn />,
      },
      {
        name: "企業案例",
        link: "/dashboard/tdp/enterprise",
        icon: <MdOutlineMenuBook />,
      },
      {
        name: "推薦提示詞",
        link: "/dashboard/tdp/recommended-prompts",
        icon: <LuCommand />,
      },
    ],
  },
  "workspace+marketing@adbert.com.tw": {
    label: "行銷團隊",
    headline: "歡迎回來，行銷團隊",
    description: "活動、公告、報名與內容管理一站式入口。",
    quickLinks: [
      {
        name: "START-ONE 報名",
        link: "/admin/form/ai-agent",
        icon: <RiRobot2Line />,
      },
      {
        name: "報名列表",
        link: "/admin/form/signed",
        icon: <BsFillPersonLinesFill />,
      },
      {
        name: "AI 智庫",
        link: "/dashboard/tdp/ai-learning-resources",
        icon: <FiFileText />,
      },
      {
        name: "活動管理",
        link: "/dashboard/tdp/events",
        icon: <MdEvent />,
      },
      {
        name: "公告管理",
        link: "/dashboard/tdp/system-announcements",
        icon: <FaBullhorn />,
      },
      {
        name: "企業案例",
        link: "/dashboard/tdp/enterprise",
        icon: <MdOutlineMenuBook />,
      },
    ],
  },
  "workspace+ceo@adbert.com.tw": {
    label: "CEO 辦公室",
    headline: "歡迎回來，CEO 辦公室",
    description: "提示詞與精選範例管理。",
    quickLinks: [
      {
        name: "提示詞管理",
        link: "/dashboard/tdp/recommended-prompts",
        icon: <LuCommand />,
      },
    ],
  },
  "workspace+ai@adbert.com.tw": {
    label: "AI Team",
    headline: "歡迎回來，AI Team",
    description: "Token 用量分析與監控。",
    quickLinks: [],
  },
  "workspace+finance@adbert.com.tw": {
    label: "財務管理",
    headline: "歡迎回來，財務管理",
    description: "發票檢視、匯出與管理。",
    quickLinks: [
      {
        name: "發票管理",
        link: "/dashboard/tdp/finance/invoices",
        icon: <MdReceiptLong />,
      },
    ],
  },
  "workspace.admin@adbert.com.tw": {
    label: "Workspace Admin",
    headline: "歡迎回來，Workspace Admin",
    description: "Workspace 與企業/個人帳號全域管理。",
    quickLinks: [
      {
        name: "Workspace",
        link: "/admin/workspace",
        icon: <MdOutlineDashboard />,
      },
      {
        name: "企業帳號管理",
        link: "/admin/company-system",
        icon: <MdOutlineMenuBook />,
      },
      {
        name: "Keystone 中控",
        link: "/dashboard/tdp/menu",
        icon: <MdOutlineDashboard />,
      },
    ],
  },
  "workspace.keystone@adbert.com.tw": {
    label: "Workspace Keystone",
    headline: "歡迎回來，Workspace Keystone",
    description: "Keystone 全部模組的管理入口。",
    quickLinks: [
      {
        name: "Keystone 中控",
        link: "/dashboard/tdp/menu",
        icon: <MdOutlineDashboard />,
      },
      {
        name: "START-ONE 報名",
        link: "/admin/form/ai-agent",
        icon: <RiRobot2Line />,
      },
      {
        name: "活動管理",
        link: "/dashboard/tdp/events",
        icon: <MdEvent />,
      },
      {
        name: "公告管理",
        link: "/dashboard/tdp/system-announcements",
        icon: <FaBullhorn />,
      },
      {
        name: "企業案例",
        link: "/dashboard/tdp/enterprise",
        icon: <MdOutlineMenuBook />,
      },
      {
        name: "AI 智庫",
        link: "/dashboard/tdp/ai-learning-resources",
        icon: <FiFileText />,
      },
      {
        name: "推薦提示詞",
        link: "/dashboard/tdp/recommended-prompts",
        icon: <LuCommand />,
      },
    ],
  },
};

const KeystoneAccountHome = () => {
  const { auth, authLoading } = useSelector(selectPermissionSettings);

  if (!auth || authLoading) {
    return null;
  }

  const config = ACCOUNT_HOME_CONFIG[auth?.email];

  if (!config) {
    return <Navigate to='/dashboard/tdp/menu' replace />;
  }

  return (
    <RightPanelLayout title={config.label}>
      <div className={styles.container}>
        <div className={styles.hero}>
          <div className={styles.heroBadge}>{config.label}</div>
          <h1 className={styles.heroTitle}>{config.headline}</h1>
          <p className={styles.heroDescription}>{config.description}</p>
        </div>

        {config.quickLinks.length > 0 ? (
          <div className={styles.cardGrid}>
            {config.quickLinks.map((link) => (
              <Link key={link.link} to={link.link} className={styles.card}>
                <div className={styles.cardIcon}>{link.icon}</div>
                <div className={styles.cardName}>{link.name}</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>功能即將上線，敬請期待。</div>
        )}
      </div>
    </RightPanelLayout>
  );
};

export default KeystoneAccountHome;
